import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { DjangoService, Routes } from '@core/services/django.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { AnalyticsEventName } from '@core/utils/AnalyticsEvents';
import {
    Configurations,
    LazyPictureComponent,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { ModalService } from '@features/modal/services/modal.service';
import { Itinerary, Trip } from '@generated/graphql';
import { LeadRequestAdditionalData } from '@generated/openapi/model/leadRequestAdditionalData';
import { LeadRequestCreatePolymorphicTypes as LeadRequestType } from '@generated/openapi/model/leadRequestCreatePolymorphicTypes';
import { PublicDeparture } from '@generated/openapi/model/publicDeparture';
import {
    DynamicFormSchemaField,
    FormDynamicWithConfigComponent,
} from '@shared/form-dynamic-with-config/form-dynamic-with-config.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';

@Component({
    selector: 'app-enquire-form-additional-info-modal',
    imports: [
        FormDynamicWithConfigComponent,
        CtaButtonComponent,
        LazyPictureComponent,
    ],
    templateUrl: './enquire-form-additional-info-modal.component.html',
    styles: ``,
})
export class EnquireFormAdditionalInfoModalComponent
    implements OnInit, OnDestroy
{
    modalData: {
        createdLeadId: string;
        trip: Trip;
        departure?: PublicDeparture;
        typeOfEnquiry: LeadRequestType;
        itinerary?: Itinerary;
        analyticsEventCompleted: AnalyticsEventName;
        analyticsEventAbandoned: AnalyticsEventName;
        analyticsEventData: {
            trip: string;
            departure?: string;
            itinerary?: string;
        };
    };
    isAdditionalInfoSubmitted = signal(false);
    readonly modalContainerstyles = [
        '!w-full !h-full !max-h-screen !rounded-none',
    ];
    readonly isModalFullscreen = true;
    featuredImageConfiguration: Configurations;

    formSchema: DynamicFormSchemaField[] = [
        {
            label: 'Any questions?',
            type: 'textbox',
            name: 'questions',
            validators: [],
        },
        {
            label: 'Would you like a callback?',
            name: 'phonenumber',
            type: 'phone',
            validators: [],
        },
    ];

    constructor(
        private djangoService: DjangoService,
        private modalService: ModalService,
        private eventTrackerService: EventTrackerService
    ) {}

    ngOnInit(): void {
        const trip = this.modalData.trip;
        const mobileCoverImage =
            trip?.enquireFormHorizontalImage || trip?.secondaryFeaturedImage;
        const desktopCoverImage =
            trip?.enquireFormVerticalImage || trip?.featuredImage;
        this.featuredImageConfiguration = {
            [Stops.FALLBACK]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 1,
                width: 600,
                width2x: 800,
            },
            [Stops.BASE]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 3,
                width: 600,
                width2x: 800,
            },
            [Stops.SM]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 3,
                width: 640,
                width2x: 1000,
            },
            [Stops.MD]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.LG]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.XL]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.XXL]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
        };
    }

    submitAdditionalInfo(values) {
        this.addDetailsToLead(this.modalData.createdLeadId, {
            comment: values.questions,
            phone_number: values.phonenumber,
        }).subscribe(() => {
            this.isAdditionalInfoSubmitted.set(true);
            this.eventTrackerService.trackEvent(
                this.modalData.analyticsEventCompleted,
                this.modalData.analyticsEventData
            );
            setTimeout(() => {
                this.closeModal();
            }, 5000);
        });
    }

    ngOnDestroy(): void {
        if (!this.isAdditionalInfoSubmitted()) {
            this.abandonLead(this.modalData.createdLeadId).subscribe();
        }
    }

    closeModal() {
        this.modalService.hide();
    }

    addDetailsToLead(id, { comment, phone_number }) {
        const route = Routes.leads;
        const resource = `${id}/add_details`;
        return this.djangoService.drfUpdate<LeadRequestAdditionalData>(
            route,
            resource,
            {
                comment,
                phone_number,
            }
        );
    }

    abandonLead(id) {
        const route = Routes.leads;
        const resource = `${id}/abandon`;
        this.eventTrackerService.trackEvent(
            this.modalData.analyticsEventAbandoned,
            this.modalData.analyticsEventData
        );
        return this.djangoService.drfCreate(route, {}, resource);
    }
}
